import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Map, Users, Receipt, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { playWelcome } from "@/lib/welcomeSound";

const DEMO_TENANT = "campad-demo";

const FEATURES = [
  { icon: Map, title: "Plattegrond & planning", text: "Plaatsen slepen, status per plaats, planningsraster per nacht en een 3D-weergave." },
  { icon: Users, title: "Gasten & reservaties", text: "Boeking, inchecken, verhuizen en uitchecken met alle gegevens van de hoofdboeker." },
  { icon: Receipt, title: "Kassa & facturatie", text: "Winkelverkoop, dagafsluiting, afstortingen en tussentijdse of definitieve afrekening." },
  { icon: Zap, title: "Tarieven & elektriciteit", text: "Tarieven per zone en periode, 6A/10A per maand en verbruik via meterstanden." },
];

export default function DemoLanding() {
  const { user, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [camp, setCamp] = useState({ camp_name: "Camping CampAd Pro" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/settings?tenant=${DEMO_TENANT}`)
      .then((r) => setCamp(r.data))
      .catch((e) => console.warn("Demo-instellingen ophalen mislukt", e?.message));
  }, []);

  if (user && user !== false) return <Navigate to="/" replace />;

  const start = async () => {
    setBusy(true);
    try {
      await demoLogin();
      playWelcome(camp.intro_music !== false);
      navigate("/");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen bg-[#0b1623] text-white overflow-hidden" data-testid="demo-landing">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)", backgroundSize: "64px 64px" }} />
      <div className="relative mx-auto max-w-5xl px-6 py-16 sm:py-24">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 grid place-items-center">
            <ShieldCheck className="h-5 w-5 text-sky-400" />
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.42em] text-white/60">CampAd Pro</span>
        </div>

        <h1 className="mt-10 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
          Bekijk {camp.camp_name || "Camping CampAd Pro"} als volledige demo
        </h1>
        <p className="mt-5 text-base text-white/60 max-w-2xl leading-relaxed">
          Een volledig gevulde camping: plaatsen op de plattegrond, gasten die vandaag aankomen en vertrekken,
          tarieven, kassa en meterstanden. Je kan alles aanpassen — elke nacht wordt de demo automatisch hersteld.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button onClick={start} disabled={busy} data-testid="demo-start-button"
            className="h-12 px-6 rounded-xl text-base font-semibold bg-sky-500 hover:bg-sky-400 text-white group">
            {busy ? "Even geduld…" : "Demo starten"}
            {!busy && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
          </Button>
          <button type="button" onClick={() => navigate("/login")} data-testid="demo-login-link"
            className="text-sm text-white/50 hover:text-white/80 underline">Zelf aanmelden</button>
          {camp.demo_cta_url && (
            <a href={camp.demo_cta_url} target="_blank" rel="noopener noreferrer" data-testid="demo-request-link"
              className="h-12 px-6 inline-flex items-center rounded-xl border border-white/15 bg-white/[0.04] text-sm font-semibold text-white/85 hover:bg-white/[0.08] transition-colors">
              Demo aanvragen
            </a>
          )}
        </div>

        <p className="mt-4 text-xs text-white/40" data-testid="demo-credentials">
          Geen registratie nodig · de demo wordt elke nacht automatisch hersteld
        </p>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid={`demo-feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <f.icon className="h-5 w-5 text-sky-400" />
              <h2 className="mt-3 font-display font-bold text-base">{f.title}</h2>
              <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
